import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { auth } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import {
  acknowledgeEmailNotice,
  EmailVerificationStatus,
  fetchEmailVerificationStatus,
  readCallableError,
  sendVerificationEmail,
  syncEmailVerification,
  EMAIL_VERIFICATION_SENT_EVENT,
} from './api';
import { computeDisplayPhase, DisplayPhase, GRACE_MS } from './phase';

const CLIENT_COOLDOWN_SEC = 60;
const FOCUS_REFRESH_MIN_INTERVAL_MS = 10_000;
const LOCKED_POLL_INTERVAL_MS = 30_000;

export type ResendResult =
  | { ok: true }
  | { ok: false; code: string; retryAfterSec?: number };

interface EmailVerificationValue {
  /** 画面状態の唯一の正本。全コンポーネントはこれを参照する */
  phase: DisplayPhase;
  email: string | null;
  /** 猶予の残り(ms)。サーバー時刻補正済み。phase が A/B のとき有効 */
  remainingMs: number;
  noticeAcked: boolean;
  pendingEmail: string | null;
  /** 公開RAG（Webサイト設置用ウィジェット）の案内を出すか。NCN は false */
  showPublicRag: boolean;
  cooldownSec: number;
  resend: () => Promise<ResendResult>;
  refresh: () => Promise<void>;
  acknowledgeNotice: () => Promise<void>;
}

const noop = async () => {};

const defaultValue: EmailVerificationValue = {
  phase: 'none',
  email: null,
  remainingMs: 0,
  noticeAcked: true,
  pendingEmail: null,
  showPublicRag: true,
  cooldownSec: 0,
  resend: async () => ({ ok: false, code: 'unavailable' }),
  refresh: noop,
  acknowledgeNotice: noop,
};

const EmailVerificationContext = createContext<EmailVerificationValue>(defaultValue);

export const useEmailVerification = () => useContext(EmailVerificationContext);

export const EmailVerificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, initializing, isEmbedMode } = useAuth();
  const [status, setStatus] = useState<EmailVerificationStatus | null>(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [offsetMs, setOffsetMs] = useState(0);
  const [tick, setTick] = useState(0);
  const [localAcked, setLocalAcked] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownSec, setCooldownSec] = useState(0);
  const lastRefreshAt = useRef(0);

  const uid = user?.uid ?? null;
  const isPassword = !!user?.providerData.some((p) => p.providerId === 'password');
  const eligible = !!user && isPassword && !isEmbedMode;
  const clientVerified = !!user?.emailVerified;

  const applyStatus = useCallback((s: EmailVerificationStatus) => {
    setStatus(s);
    setOffsetMs(s.serverNowMs - Date.now());
    setStatusFailed(false);
  }, []);

  // ユーザーが変わったら状態をリセット
  useEffect(() => {
    setStatus(null);
    setStatusFailed(false);
    setLocalAcked(false);
    setCooldownUntil(0);
  }, [uid]);

  // サーバーから状態（作成日時・適用対象・案内表示済み）を取得
  useEffect(() => {
    if (!eligible || clientVerified || initializing) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const load = async () => {
      try {
        const s = await fetchEmailVerificationStatus();
        if (!cancelled) applyStatus(s);
      } catch (e) {
        console.warn('[emailVerification] status fetch failed', readCallableError(e).code);
        if (!cancelled) {
          setStatusFailed(true);
          timer = setTimeout(load, 20_000);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [eligible, clientVerified, initializing, uid, applyStatus]);

  const refresh = useCallback(async () => {
    const current = auth?.currentUser;
    if (!current) return;
    lastRefreshAt.current = Date.now();
    try {
      await current.reload();
      await current.getIdToken(true);
    } catch (e) {
      console.warn('[emailVerification] reload/getIdToken failed', e);
    }
    try {
      await syncEmailVerification();
    } catch (e) {
      console.warn('[emailVerification] sync failed', readCallableError(e).code);
    }
    try {
      applyStatus(await fetchEmailVerificationStatus());
    } catch (e) {
      console.warn('[emailVerification] status refresh failed', readCallableError(e).code);
    }
    setTick((n) => n + 1);
  }, [applyStatus]);

  const email = user?.email ?? null;
  const unverified = eligible && !clientVerified;

  const phase: DisplayPhase = useMemo(() => {
    if (!eligible) return 'none';
    if (initializing) return 'loading';
    if (clientVerified) return 'none';
    if (!status) return statusFailed ? 'none' : 'loading';
    if (status.emailVerified) return 'none';
    return computeDisplayPhase({
      emailVerified: false,
      applicable: status.applicable,
      creationMs: status.creationMs,
      nowMs: Date.now() + offsetMs,
    });
    // tick は下の setInterval による再計算のため
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, initializing, clientVerified, status, statusFailed, offsetMs, tick]);

  const remainingMs = status ? Math.max(0, status.creationMs + GRACE_MS - (Date.now() + offsetMs)) : 0;

  // サーバー側で認証済みなのにクライアントが未反映なら同期する
  useEffect(() => {
    if (status?.emailVerified && !clientVerified) void refresh();
  }, [status?.emailVerified, clientVerified, refresh]);

  // 表示を更新するためのタイマー（猶予の残り時間・フェーズ境界）
  useEffect(() => {
    if (!unverified) return;
    const id = setInterval(() => setTick((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, [unverified]);

  // 登録直後の送信でシリーズ(ncn/ncg)がサーバーに保存されたら状態を取り直す
  useEffect(() => {
    const onSent = () => void refresh();
    window.addEventListener(EMAIL_VERIFICATION_SENT_EVENT, onSent);
    return () => window.removeEventListener(EMAIL_VERIFICATION_SENT_EVENT, onSent);
  }, [refresh]);

  // 画面復帰・フォーカス復帰で最新状態を再確認
  useEffect(() => {
    if (!unverified) return;
    const onWake = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastRefreshAt.current < FOCUS_REFRESH_MIN_INTERVAL_MS) return;
      void refresh();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [unverified, refresh]);

  // ロック中は別ブラウザ等での認証完了を拾うため定期的に再確認（永久ロック防止）
  useEffect(() => {
    if (phase !== 'C') return;
    const id = setInterval(() => {
      if (document.visibilityState !== 'hidden') void refresh();
    }, LOCKED_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phase, refresh]);

  // クライアント側クールダウン表示（サーバー側制限は別途強制される）
  useEffect(() => {
    if (cooldownUntil <= Date.now()) {
      setCooldownSec(0);
      return;
    }
    const update = () => setCooldownSec(Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000)));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const resend = useCallback(async (): Promise<ResendResult> => {
    if (cooldownUntil > Date.now()) {
      return { ok: false, code: 'COOLDOWN', retryAfterSec: Math.ceil((cooldownUntil - Date.now()) / 1000) };
    }
    // 多重送信防止: 先にクールダウンを開始してから送る
    setCooldownUntil(Date.now() + CLIENT_COOLDOWN_SEC * 1000);
    try {
      const r = await sendVerificationEmail();
      if (r.status === 'already_verified') void refresh();
      return { ok: true };
    } catch (e) {
      const err = readCallableError(e);
      if (err.retryAfterSec) setCooldownUntil(Date.now() + err.retryAfterSec * 1000);
      else setCooldownUntil(0);
      return { ok: false, code: err.code, retryAfterSec: err.retryAfterSec };
    }
  }, [cooldownUntil, refresh]);

  const acknowledgeNotice = useCallback(async () => {
    setLocalAcked(true);
    try {
      await acknowledgeEmailNotice();
    } catch (e) {
      console.warn('[emailVerification] acknowledge failed', readCallableError(e).code);
    }
  }, []);

  const value: EmailVerificationValue = {
    phase,
    email,
    remainingMs,
    noticeAcked: localAcked || !!status?.noticeAcked,
    pendingEmail: status?.pendingEmail ?? null,
    showPublicRag: status?.series !== 'ncn',
    cooldownSec,
    resend,
    refresh,
    acknowledgeNotice,
  };

  return <EmailVerificationContext.Provider value={value}>{children}</EmailVerificationContext.Provider>;
};
